import { Direction } from '@angular/cdk/bidi';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AbstractControl, ValidatorFn } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import {
  EligibilityResultDialogComponent,
  EligibilityResultDialogData,
} from '@shared/components/eligibility-result-dialog/eligibility-result-dialog.component';
import { MemberRegistrationResultComponent } from '@shared/components/member-registration-result/member-registration-result.component';
import { Product } from '@shared/models/product';
import { DataLayerService } from '@shared/services/data-layer.service';
import { ApiEndpoint } from '@shared/shared/enums/api-endpoints.enum';
import { HomePageComponentNameEnum } from '@shared/shared/enums/homepage-components-name.enum';
import { CreditCardService } from '@shared/shared/services/credit-card.service';
import { Observable, Subject, take } from 'rxjs';
import { SiteSettingsService } from './site-settings.service';

@Injectable({ providedIn: 'root' })
export class ProductHomepageService {
  public static Validators = {
    validCard: (acceptEligibilityBin = false): ValidatorFn => {
      return (control: AbstractControl): { [key: string]: any } => {
        const cardNumber = control.value;

        let isValidCardNumber: boolean;
        if (acceptEligibilityBin) {
          isValidCardNumber = /^([\d]){6,19}$/.test(cardNumber);
        } else {
          isValidCardNumber = CreditCardService.isValidCard(cardNumber);
        }

        return !isValidCardNumber ? { invalidCardNumber: true } : null;
      };
    },
  };

  public eligibilityCheckLoading = false;
  public eligibilityCheckValidationErrors = [];

  constructor(
    private router: Router,
    private dataLayer: DataLayerService,
    private http: HttpClient,
    private dialog: MatDialog,
    private siteSettingsService: SiteSettingsService
  ) {}

  public navigateToUserEligible(token?: string) {
    localStorage.setItem('uuidToken', token);
    window.location.href = '/product/user-eligible';
  }

  public navigateToUserIneligible() {
    this.router.navigateByUrl('/product/user-ineligible');
  }

  public navigateToUserExists() {
    this.router.navigateByUrl('/product/user-exists');
  }

  public navigateToRegistration() {
    this.router.navigateByUrl('/registration');
  }

  public getProducts(): Observable<Product[]> {
    return this.http.get<Product[]>(ApiEndpoint.ELIGIBILITY_CHECK);
  }

  public checkEligibility(data, eligibilityType, homePageComponent?, navigateOnSuccess = true): any {
    const navigateSubject = new Subject<void>();
    this.eligibilityCheckLoading = true;
    this.dataLayer.checkEligibility(data, eligibilityType).subscribe(
      (res) => {
        if (res && res?.siteId) {
          this.siteSettingsService.syncSiteId(res.siteId);
        }

        this.eligibilityCheckLoading = false;
        if (res['already_exists']) {
          this.navigateToUserExists();
        } else if (navigateOnSuccess) {
          if (homePageComponent === HomePageComponentNameEnum.ProductHomepageMastercardComponent) {
            this.openEligibilityResultPopup(
              'success',
              'eligibility_success_subtitle',
              'MC Registration - Eligibility success pop up',
              ['UserEligibleMastercard:Paragraph1'],
              ['user_eligible_mastercard_1']
            );
          } else {
            if (!res['errors'] || res['errors'].length === 0) {
              this.navigateToUserEligible(res?.token);
            } else {
              this.navigateToUserIneligible();
            }
          }
        } else {
          if (homePageComponent === HomePageComponentNameEnum.ProductHomepageMastercardComponent) {
            if (res.isEligible) {
              this.openEligibilityResultPopup(
                'success',
                'eligibility_success_subtitle',
                'MC Registration - Eligibility success pop up',
                ['UserEligibleMastercard:Paragraph1'],
                ['user_eligible_mastercard_1']
              );
            } else {
              this.openEligibilityResultPopup(
                'invalid',
                'eligibility_unsuccessful_subtitle',
                'MC Registration - Eligibility fail pop up',
                ['UserIneligibleMastercard:Paragraph1', 'UserIneligibleMastercard:Paragraph2'],
                ['user_ineligible_mastercard_1']
              );
            }
          } else {
            navigateSubject.next();
          }
        }
        navigateSubject.complete();
      },
      (validationErrors: []) => {
        if (!validationErrors.length) {
          if (navigateOnSuccess) {
            if (homePageComponent === HomePageComponentNameEnum.ProductHomepageMastercardComponent) {
              this.openEligibilityResultPopup(
                'invalid',
                'eligibility_unsuccessful_subtitle',
                'MC Registration - Eligibility fail pop up',
                ['UserIneligibleMastercard:Paragraph1', 'UserIneligibleMastercard:Paragraph2'],
                ['user_ineligible_mastercard_1']
              );
            } else {
              this.navigateToUserIneligible();
            }
          } else {
            navigateSubject.error('');
          }
        }
        this.eligibilityCheckValidationErrors = validationErrors;
        this.eligibilityCheckLoading = false;
        navigateSubject.complete();
      }
    );
    return navigateSubject;
  }

  public openEligibilityResultPopup(
    type: 'invalid' | 'success',
    subtitle: string,
    tracking: string,
    translations: string[],
    customVarParagraph: string[]
  ) {
    let preloginOn = false;
    this.siteSettingsService
      .fetchSiteSettings()
      .pipe(take(1))
      .subscribe((data) => {
        preloginOn = data.switches.pre_login_home_page;
      });
    const direction = document.querySelector('html')?.dir as Direction;
    this.dialog.closeAll();
    // if prelogin feature is off success, error messages are in popup, else they go to their pages
    if (preloginOn) {
      if (type === 'invalid') {
        this.router.navigate(['/product/user-ineligible']);
      } else if (type === 'success') {
        this.router.navigate(['/product/user-eligible']);
      }
      return;
    }
    this.dialog.open<EligibilityResultDialogComponent, EligibilityResultDialogData>(EligibilityResultDialogComponent, {
      disableClose: true,
      direction,
      data: {
        type: type,
        subtitle: subtitle,
        tracking: tracking,
        translations: translations,
        customVarParagraph: customVarParagraph,
      },
    });
  }

  public memberRegistration(data) {
    this.dataLayer.newMemberRegistration(data).subscribe(
      (res) => {
        if (res && res.status) {
          this.navigateToMemberRegistration(res.status);
        }
      },
      () => {
        this.navigateToMemberRegistration('');
      }
    );
  }

  public navigateToMemberRegistration(status: any) {
    const direction = document.querySelector('html')?.dir ? (document.querySelector('html').dir as Direction) : 'ltr';

    this.dialog.open(MemberRegistrationResultComponent, {
      panelClass: 'popup-member-registration-result',
      data: status,
      disableClose: true,
      direction,
    });
  }
}
